import React, { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faCopy, 
  faDownload, 
  faClipboard 
} from "@fortawesome/free-solid-svg-icons";
import QRCode from "qrcode";
import copyLinkIllustration from "../../assets/img/illustrations/copy-link-illustration.png";
import facebookLogo from "../../assets/img/icons/facebook.svg";
import whatsAppLogo from "../../assets/img/icons/whatsapp.svg";
import MediaShareButton from "./ShareToMediaButton";


interface ShareProfileModalProps {
  show: boolean;
  onHide: () => void;
  providerName: string;
  uniqueName?: string;
}

const ShareProfileModal: React.FC<ShareProfileModalProps> = ({
  show,
  onHide,
  providerName,
  uniqueName
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [profileUrl, setProfileUrl] = useState<string>("");
  const [isGeneratingQR, setIsGeneratingQR] = useState<boolean>(false);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);

  // Generate the profile URL
  useEffect(() => {
    if (uniqueName) {
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/home/store/${uniqueName}`;
      setProfileUrl(url);
    }
  }, [uniqueName]);

  // Generate QR code when modal is shown and profile URL is available
  useEffect(() => {
    if (show && profileUrl) {
      console.log('Generating QR code for URL:', profileUrl);
      generateQRCode(profileUrl);
    }
  }, [show, profileUrl]);

  const generateQRCode = async (url: string) => {
    try {
      setIsGeneratingQR(true);
      const canvas = qrCanvasRef.current;
      
      if (!canvas) {
        setIsGeneratingQR(false);
        return;
      }
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }

      await QRCode.toCanvas(canvas, url, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      
      console.log('QR code generated successfully on canvas');
      
      // Also generate data URL for download
      const dataUrl = await QRCode.toDataURL(url, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
      setQrCodeDataUrl(dataUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = profileUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadQRCode = () => {
    if (qrCodeDataUrl) {
      const link = document.createElement('a');
      link.download = `${providerName}-qr-code.png`;
      link.href = qrCodeDataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      aria-labelledby="share-profile-modal"
      centered
      dialogClassName="share-profile-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title id="share-profile-modal" className="w-100">
          {/* No icon as per Figma design */}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="p-0">
        <div className="d-flex flex-column align-items-center" style={{ gap: '1rem' }}>
          {/* Row 1: Title */}
          <div className="text-center px-3">
            <h4 
              className="mb-0 text-nunito-heading-3"
              style={{
                fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
                lineHeight: '1.2'
              }}
            >
              Comparte el enlace a este proveedor
            </h4>
          </div>

          {/* Row 2: URL Input */}
          <div className="w-100 d-flex justify-content-center px-3">
            <div 
              className="d-flex align-items-center px-3 w-100"
              style={{
                maxWidth: '450px',
                height: '38px',
                background: '#FFFFFF',
                border: '1px solid #CBD0DD',
                borderRadius: '6px'
              }}
            >
              <div className="text-center flex-grow-1">
                <span
                  className="text-nunito-12-8-semibold"
                  style={{
                    fontSize: 'clamp(0.7rem, 2.5vw, 0.8rem)',
                    wordBreak: 'break-all',
                    color: 'blue'
                  }}
                >
                  {profileUrl}
                </span>
              </div>
            </div>
          </div>

          {/* Row 3: Two columns - QR and Copy Link */}
          <div className="w-100 px-3">
            <div className="row g-4 justify-content-center align-items-center">
              {/* Column 1: QR Code */}
              <div className="col-12 col-md-6 d-flex flex-column align-items-center">
                <div 
                  className="d-flex justify-content-center align-items-center mb-3"
                  style={{
                    width: 'min(120px, 25vw)',
                    height: 'min(120px, 25vw)',
                    minWidth: '80px',
                    minHeight: '80px',
                    border: '4px solid #85A9FF',
                    borderRadius: '6px',
                    background: '#FFFFFF'
                  }}
                >
                  {isGeneratingQR ? (
                    <div className="spinner-border text-primary" style={{ width: '24px', height: '24px' }}>
                      <span className="visually-hidden">Generando...</span>
                    </div>
                  ) : qrCodeDataUrl ? (
                    <img 
                      src={qrCodeDataUrl} 
                      alt="QR Code" 
                      className="w-100 h-100"
                      style={{ borderRadius: '2px', objectFit: 'contain' }}
                    />
                  ) : (
                    <canvas 
                      ref={qrCanvasRef}
                      className="w-100 h-100"
                      style={{ display: 'none' }}
                    />
                  )}
                </div>
                
                {/* Download QR Button */}
                <button
                  onClick={downloadQRCode}
                  disabled={!qrCodeDataUrl || isGeneratingQR}
                  className="btn d-flex align-items-center justify-content-center"
                  style={{
                    background: '#F5F8FF',
                    borderRadius: '6px',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    gap: '0.5rem',
                    minWidth: '140px'
                  }}
                >
                  <FontAwesomeIcon icon={faDownload} style={{ fontSize: '12px' }} />
                  <span className="text-nunito-12-8-bold" style={{ color: '#003CC7' }}>
                    Descargar QR
                  </span>
                </button>
              </div>

              {/* Column 2: Copy to Clipboard */}
              <div className="col-12 col-md-6 d-flex flex-column align-items-center">
                {/* Illustration/Icon */}
                <div 
                  className="d-flex justify-content-center align-items-center mb-3"
                  style={{
                    width: 'min(120px, 25vw)',
                    height: 'min(120px, 25vw)',
                    minWidth: '80px',
                    minHeight: '80px',
                    borderRadius: '8px',
                    background: '#FFFFFF'
                  }}
                >
                  <img 
                    src={copyLinkIllustration} 
                    alt="Copy Link" 
                    style={{
                      width: 'clamp(80px, 30vw, 100px)',
                      height: 'clamp(80px, 30vw, 100px)',
                      objectFit: 'contain'
                    }}
                  />
                </div>
                
                {/* Copy Button */}
                <button
                  onClick={handleCopyToClipboard}
                  className="btn d-flex align-items-center justify-content-center"
                  style={{
                    background: copySuccess ? '#28a745' : '#3874FF',
                    borderRadius: '6px',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    gap: '0.5rem',
                    minWidth: '140px'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={copySuccess ? faClipboard : faCopy} 
                    style={{ fontSize: '12px', color: '#FFFFFF' }} 
                  />
                  <span className="text-nunito-12-8-bold" style={{ color: '#FFFFFF' }}>
                    {copySuccess ? 'Copiado!' : 'Copiar Enlace'}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Row 4: Social Media Text */}
          <div className="text-center px-3">
            <h6 
              className="mb-0 text-nunito-12-8-bold"
              style={{
                color: '#222834',
                fontSize: 'clamp(0.7rem, 2.5vw, 0.8rem)'
              }}
            >
              O comparte directo en tus redes sociales:
            </h6>
          </div>

          {/* Row 5: Social Media Buttons */}
          <div className="d-flex justify-content-center align-items-center flex-wrap gap-3 mb-3">
          
            <MediaShareButton mediaName="facebook" logo={facebookLogo} />
            <MediaShareButton mediaName="whatsapp" logo={whatsAppLogo} />
            
           
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ShareProfileModal;